import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Stops one broken panel from blanking the whole portal.
 *
 * GP4 in docs/audits/2026-08-10-green-pipeline.md. On 2026-08-09 a single
 * expression in the product edit modal threw during render -
 * `d.slugs.includes(...)` against a module that ships without `slugs` - and
 * because React unmounts the tree on an unhandled render error, the entire App
 * went white. A client would have seen a blank page and had nothing to report
 * beyond "it stopped working".
 *
 * This does not prevent that class of defect; the smoke tests in GP2 are what
 * catch it before it ships. What it changes is the blast radius when one does
 * get through: "this panel failed, here is what to do" instead of nothing at
 * all, and the rest of the screen still works.
 *
 * A class component because React has no hook equivalent - `componentDidCatch`
 * and `getDerivedStateFromError` exist only on classes. This is the one place
 * in the portal that needs one.
 */

interface Props {
  children: ReactNode;
  /** Names the area, so the message can say which part failed. */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is where this gets diagnosed. Deliberately not sent anywhere:
    // a render error can carry whatever was being rendered, and this App shows
    // clients' data - shipping that to a third-party collector would be a new
    // processor for personal data, which is not a decision a logging line makes.
    console.error(`Render failed${this.props.label ? ` in ${this.props.label}` : ''}:`,
      error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="card space-y-3 p-5" role="alert">
        <div>
          <h2 className="font-semibold">
            {this.props.label ? `${this.props.label} could not be displayed` : 'Something broke'}
          </h2>
          <p className="mt-1 text-sm text-ink-600 dark:text-paper-300">
            The rest of the App is still working. Reloading usually clears it; if it
            keeps happening, this is a fault on our side rather than anything you did.
          </p>
        </div>

        {/* The message, not the stack. It is what makes a report actionable,
            and it is already in the reader's console if they need more. */}
        <p className="rounded-lg bg-paper-200 p-3 font-mono text-xs dark:bg-ink-700">
          {error.message || String(error)}
        </p>

        <div className="flex gap-2">
          <button
            type="button"
            className="btn-primary"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => window.location.reload()}
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
